/** データセットに含まれる 1 ファイル。name はマスキング後の表示名（GI-10 / GC-12） */
export interface DashboardFileNode {
	readonly id: string;
	readonly name: string;
}

/**
 * 選択できないデータセットの理由。文言は client が i18n キーに写す（サーバに文言を持たせない）。
 * - fileCountMismatch: フォルダ内の tif が 2 件でない（1 件 / 3 件以上。tif を含まないフォルダは一覧に出ない）
 * - sameRole: 2 件とも地形、または 2 件とも画像と判別され組にならない
 */
export type DtmOrthoDatasetRejection = "fileCountMismatch" | "sameRole";

/** 選択不可の理由 -> i18n キー（GI-39） */
export const DTM_ORTHO_DATASET_REJECTION_KEYS: Readonly<
	Record<DtmOrthoDatasetRejection, string>
> = {
	fileCountMismatch: "DTM_ORTHO_DATASET_FILE_COUNT_MISMATCH",
	sameRole: "DTM_ORTHO_DATASET_SAME_ROLE",
};

/** データセットの共通項目。id は Survey 配下のデータセットフォルダのノード ID */
interface DtmOrthoDatasetBase {
	readonly id: string;
	readonly name: string;
	/** 点群カテゴリの既存の並び替え規則に合わせる（Number() で比較） */
	readonly created_at: string;
}

/**
 * GET /v2/api/:siteId/dtm-ortho/dashboard-datasets の datasets の 1 要素（API 形式。bucket_id はトップレベル）。
 * resolved   = 両方判別でき DTM とオルソの組になった（選択可。ダイアログの初期値 = 自動判別の結果）
 * unresolved = ヘッダを読めない / 判別できない / 片方だけ判別できた（選択可。初期値なし = ダイアログの既定値）
 * rejected   = 選択不可（理由つき）
 */
export type DtmOrthoDatasetResponseItem =
	| (DtmOrthoDatasetBase & {
			readonly kind: "resolved";
			readonly image: DashboardFileNode;
			readonly terrain: DashboardFileNode;
	  })
	| (DtmOrthoDatasetBase & {
			readonly kind: "unresolved";
			/** 名前順。ユーザーが種別選択ダイアログで割り当てる（GI-38） */
			readonly files: readonly [DashboardFileNode, DashboardFileNode];
	  })
	| (DtmOrthoDatasetBase & {
			readonly kind: "rejected";
			readonly reason: DtmOrthoDatasetRejection;
	  });

/** GET /v2/api/:siteId/dtm-ortho/dashboard-datasets に同梱される許可拡張子。画面の拡張子ラベルの唯一の出どころ（GI-31） */
export interface AssetLinkExtensions {
	readonly pointCloud: readonly string[];
	readonly design: readonly string[];
	readonly dtmOrtho: readonly string[];
}

/** GET /v2/api/:siteId/dtm-ortho/dashboard-datasets の応答。0 件でも datasets: [] で返る */
export interface DtmOrthoDatasetListResponse {
	readonly bucket_id: string;
	readonly type: string;
	readonly datasets: readonly DtmOrthoDatasetResponseItem[];
	readonly extensions: AssetLinkExtensions;
}

/**
 * 画面が扱うデータセット 1 件。取込に使う resolved / unresolved は応答の bucket_id を各要素に写して持つ。
 * rejected は選択不可なので bucketId を持たない
 */
export type DashboardDtmOrthoDataset =
	| (Extract<DtmOrthoDatasetResponseItem, { kind: "resolved" }> & {
			readonly bucketId: string;
	  })
	| (Extract<DtmOrthoDatasetResponseItem, { kind: "unresolved" }> & {
			readonly bucketId: string;
	  })
	| Extract<DtmOrthoDatasetResponseItem, { kind: "rejected" }>;

/** 種別選択に進めるデータセット（選択可の 2 種） */
export type SelectableDtmOrthoDataset = Exclude<
	DashboardDtmOrthoDataset,
	{ kind: "rejected" }
>;
